'use memo';

import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AgxRow,
  AgxColumn,
  AgxHeader,
  AgxLabel,
  Images,
  AgxBodyText,
  AgxSlideUpModal,
  AgxMultiOptionButton,
  DocumentTypesMap,
  DocumentTypes,
  StakeholderType,
  SendConfiguration,
  RenderAnimation,
  RenderAnimationType,
  AustralianState,
  MultiOptionButtonOption,
  FormConfigType,
  VendorBuyer,
  cleanFullAddress,
  UploadedDocument,
} from '@urbanx/agx-ui-components';
import {
  AgxModal,
  AgxUploadedDocumentPdfEditor,
  AgxButton,
  AgxSpacer,
  Icon_DownloadOutline,
} from '@urbanx/agx.ui-components2';
import type { PdfEditorSaveStatus } from '@urbanx/agx.ui-components2';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  useGetFileLink,
  useUploadFile,
} from 'components/ui-components/File/fileApi';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useFormClearer } from 'hooks/useFormCleaner';
import { useFormSettings } from 'hooks/useFormSettings';
import usePropertyImageSelector from 'selectors/usePropertyImageSelector';
import useFormDocumentsSelector from 'selectors/useFormDocumentsSelector';
import useFormId from 'hooks/useFormId';
import useFormCampaignId from 'hooks/useFormCampaignId';
import ErrorModal from 'helpers/ErrorModal';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { markCampaignsAsStale } from '../../campaigns/campaignsReducer';
import { completeCampaignStep, sendForm } from 'Api/Campaigns/campaignsApi';
import {
  loadFormConfig,
  updateGeneratedFormDocument,
} from '../../form/formReducer';
import { setAndShowErrorToast } from 'store/config';
import { PreparedSignatory, prepareSignatories } from 'utils/vendorBuyerUtil';
import placeHolderImage from 'assets/images/placeholder-property.png';
import contractPreview from 'assets/images/contract-preview.png';
import SendConfirmation from './SendConfirmation';
import './ContractReviewAndSend.scss';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { sortDocumentsByType, sortDocumentsByTypes } from 'utils/documentUtil';
import {
  completeMarketingPackageStep,
  sendMarketingPackage,
} from 'Api/Marketing/marketingApi';
import { FormType } from '@urbanx/agx.campaigns.client';
import { buildContractExchangeInitialsTemplate } from './contractExchangeInitialsPdfTemplate';
import { useCampaignStaleMarker } from 'hooks/useCampaignStaleMarker';

interface ContractReviewAndSignProps {
  goToFirstPage: () => void;
  goToPreviousPage: () => void;
  vendors?: VendorBuyer[];
  formName?: string;
  sendViaDocuSign: boolean;
  disableFormSubmission?: boolean;
  sendConfigurations: SendConfiguration[];
}

const ContractReviewAndSign = (props: ContractReviewAndSignProps) => {
  const {
    goToFirstPage,
    goToPreviousPage,
    vendors,
    formName,
    sendViaDocuSign,
    disableFormSubmission,
    sendConfigurations,
  } = props;
  const updateFormSettings = useFormSettings();
  const markCampaignAsStale = useCampaignStaleMarker();
  const formId = useFormId();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [buyerSignatories, setBuyerSignatories] = useState<PreparedSignatory[]>(
    []
  );
  const [vendorSignatories, setVendorSignatories] =
    useState<PreparedSignatory[]>();
  const [showDocuSignLoader, setShowDocuSignLoader] = useState(false);
  const [showSuccessLoader, setShowSuccessLoader] = useState(false);
  const isMobile = ScreenSize() === Breakpoints.Mobile;
  const isDesktop = ScreenSize() === Breakpoints.Desktop;
  const { formErrors: errors, downloadDocumentTypes } = useAppSelector(
    state => state.form
  );
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const { medium } = usePropertyImageSelector() || {};
  const documents = useFormDocumentsSelector();
  const getFileLink = useGetFileLink();
  const uploadFile = useUploadFile();
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const clearFormData = useFormClearer();
  const [showRecipients, setShowRecipients] = useState(false);
  const [configuration, setConfiguration] = useState(sendConfigurations[0]);
  const [disableButtons, setDisableButtons] = useState(false);
  const [showPdfEditor, setShowPdfEditor] = useState(false);
  const [pdfEditorSaveStatus, setPdfEditorSaveStatus] =
    useState<PdfEditorSaveStatus>('idle');
  const [buyerSolicitorRecipient, setBuyerSolicitorRecipient] =
    useState<PreparedSignatory>();
  const [vendorSolicitorRecipient, setVendorSolicitorRecipient] =
    useState<PreparedSignatory>();

  const {
    selectedForm,
    formValues: {
      Buyers: campaignBuyers,
      BuyerSolicitor: buyerSolicitor,
      VendorSolicitor: vendorSolicitor,
    },
    address,
  } = useAppSelector(state => state.form);
  const campaignId = useFormCampaignId();
  const isContractExchange =
    selectedForm?.formType === FormType.ContractExchange;
  const isNswContractExchange =
    isContractExchange && selectedForm?.state === AustralianState.NSW;
  const generatedContract = documents.find(
    document => document.documentType === DocumentTypes.ContractExchange
  );
  const generatedSalesAdvice = documents.find(
    document => document.documentType === DocumentTypes.SalesAdvice
  );
  const listingAddress = address ? cleanFullAddress(address) : 'this property';

  const {
    agents: { loadingState: agentsLoaded, items: agents },
  } = useAppSelector(state => state.agencies);

  const documentTypes = documents.map(doc => doc.documentType);
  const uniqueTypes = new Set(documentTypes);

  const noAttachments =
    uniqueTypes.size === 2 && uniqueTypes.has(DocumentTypes.ContractExchange);

  const fileDownloadOptions = useMemo(() => {
    if (documents.length === 0) return [];
    const options: MultiOptionButtonOption[] = [];
    const hasCustomDownloads =
      downloadDocumentTypes && downloadDocumentTypes.length > 0;

    const filteredDocuments = documents.filter(
      document =>
        !hasCustomDownloads ||
        downloadDocumentTypes.includes(document.documentType)
    );

    const orderedDocuments = !hasCustomDownloads
      ? sortDocumentsByType(filteredDocuments)
      : sortDocumentsByTypes(filteredDocuments, downloadDocumentTypes);

    orderedDocuments.forEach(d => {
      options.push({
        text: d.documentType
          ? DocumentTypesMap[d.documentType as keyof typeof DocumentTypesMap]
          : '',
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    return options;
  }, [documents, downloadDocumentTypes, getFileLink, isDesktop]);

  const contractExchangeFieldBlockTemplates = useMemo(() => {
    const initialsTemplate = buildContractExchangeInitialsTemplate({
      vendorSignatories,
      buyerSignatories,
    });

    return initialsTemplate ? [initialsTemplate] : [];
  }, [buyerSignatories, vendorSignatories]);

  useEffect(() => {
    updateFormSettings({
      formBackgroundInverted: showDocuSignLoader || showSuccessLoader,
      growContent: showDocuSignLoader || showSuccessLoader,
    });
  }, [showDocuSignLoader, showSuccessLoader]);

  const beginSubmission = async () => {
    if (!getAuthToken || !campaignId || !formId) return;

    if (selectedForm?.formConfigType === FormConfigType.Campaign) {
      const contractSubmissionForm = availableForms.find(
        form =>
          form.type === FormType.ContractSubmission &&
          isEqual(form.state, selectedForm?.state)
      );

      if (contractSubmissionForm == null) {
        return;
      }
    }

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newForm =
        selectedForm?.formConfigType === FormConfigType.Campaign
          ? await completeCampaignStep(authToken, campaignId, true, formId)
          : await completeMarketingPackageStep(
              authToken,
              campaignId,
              formId,
              true
            );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableButtons(false);
        return;
      }

      dispatch(loadFormConfig(newForm));
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
    }

    setDisableButtons(false);
  };

  const sendEnvelope = async (
    emailVendorNow: boolean,
    signingOrderGroups?: StakeholderType[],
    includeSolicitorsInSalesAdvice?: boolean
  ) => {
    if (!getAuthToken || !campaignId) return;

    try {
      setShowDocuSignLoader(true);
      updateFormSettings({
        showBreadcrumbs: false,
        displayTitleVisible: false,
      });

      const authToken = await getAuthToken();

      if (!authToken) {
        updateFormSettings({
          showBreadcrumbs: true,
          displayTitleVisible: true,
        });
        setShowDocuSignLoader(false);
        return;
      }

      const sendFormSubmission = {
        campaignId,
        formId: formId || '',
        formType: selectedForm?.formType as FormType,
        state: selectedForm?.state as AustralianState,
        digitalSignatureSubmission: {
          sendVendorsNow: emailVendorNow,
          signingOrderGroups: isContractExchange
            ? signingOrderGroups
            : undefined,
          includeSolicitorsInSalesAdvice: isContractExchange
            ? includeSolicitorsInSalesAdvice
            : undefined,
        },
        documents:
          isContractExchange && generatedContract
            ? [generatedContract]
            : undefined,
      };

      if (selectedForm?.formConfigType === FormConfigType.Marketing) {
        await sendMarketingPackage(authToken, sendFormSubmission);
      }

      if (selectedForm?.formConfigType === FormConfigType.Campaign) {
        await sendForm(authToken, sendFormSubmission);
        dispatch(markCampaignsAsStale());
        void markCampaignAsStale(campaignId).catch(console.error);
      }

      setTimeout(() => {
        setShowDocuSignLoader(false);
        setShowSuccessLoader(true);
        setTimeout(() => {
          clearFormData();
          navigate('/');
        }, 1800);
      }, 1500);
    } catch (err: any) {
      updateFormSettings({
        showBreadcrumbs: true,
        displayTitleVisible: true,
      });
      setShowDocuSignLoader(false);
      console.error(err);
      dispatch(setAndShowErrorToast(err.message));
    }
  };

  useEffect(() => {
    if (vendors) {
      setVendorSignatories(prepareSignatories(vendors, StakeholderType.Vendor));
    }
  }, [agents, agentsLoaded, vendors]);

  useEffect(() => {
    if (campaignBuyers) {
      setBuyerSignatories(
        prepareSignatories(campaignBuyers, StakeholderType.Buyer)
      );
    }
  }, [campaignBuyers]);

  useEffect(() => {
    if (buyerSolicitor) {
      setBuyerSolicitorRecipient({
        id: buyerSolicitor.solicitor?.solicitorName,
        name: buyerSolicitor.solicitor?.solicitorName,
        email: buyerSolicitor.solicitor?.solicitorEmail,
        type: StakeholderType.BuyerSolicitor,
      });
    }
  }, [buyerSolicitor]);

  useEffect(() => {
    if (vendorSolicitor) {
      setVendorSolicitorRecipient({
        id: vendorSolicitor.solicitor?.solicitorName,
        name: vendorSolicitor.solicitor?.solicitorName,
        email: vendorSolicitor.solicitor?.solicitorEmail,
        type: StakeholderType.VendorSolicitor,
      });
    }
  }, [vendorSolicitor]);

  const confirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>Starting a submission?</AgxHeader>
      <AgxBodyText medium>
        You will not be able to edit the {formName} once you continue.
      </AgxBodyText>
      <AgxBodyText medium>Continue to submission?</AgxBodyText>
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          size="large"
          variant="primary"
          text="Yes, start submission"
          onClick={beginSubmission}
          disabled={disableButtons}
        />
        <AgxButton
          id="cancel"
          variant="hollow"
          size="large"
          text="No, go back"
          onClick={() => setShowConfirmationModel(false)}
          disabled={disableButtons}
        />
      </div>
    </AgxColumn>
  );

  const downloadSalesAdviceButton = (text = 'Download the Sales Advice') =>
    generatedSalesAdvice ? (
      <AgxButton
        id="btn-download-sales-advice"
        text={text}
        variant='link'
        size='large'
        onClick={async () => {
          const fileLink = await getFileLink(
            generatedSalesAdvice.containerFilePath
          );
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        }}
        disabled={disableButtons}
        rightIcon={
          <Icon_DownloadOutline
            height={20}
            width={20}
            color="currentColor"
          />
        }
      />
    ) : null;

  const viewEditContractButton = (variant: 'primary' | 'hollow' = 'hollow') =>
    generatedContract ? (
      <AgxButton
        id="btn-view-edit-contract"
        text="View and edit Contract"
        variant={variant}
        size='large'
        onClick={() => setShowPdfEditor(true)}
        disabled={disableButtons}
      />
    ) : null;

  const viewPdfButton = () => {
    if (isContractExchange) {
      if (!generatedContract) return;

      if (isNswContractExchange) {
        return downloadSalesAdviceButton('Download Sales Advice');
      }

      return (
        <AgxSpacer
          direction="vertical"
          gap="spacing-2xl"
          contentAlignment="center"
        >
          {viewEditContractButton()}
          {downloadSalesAdviceButton()}
        </AgxSpacer>
      );
    }

    if (fileDownloadOptions.length === 0) return;

    if (fileDownloadOptions.length === 1 || noAttachments) {
      return (
        <AgxButton
          id="btn-download"
          text="Download"
          variant="hollow"
          size="medium"
          onClick={fileDownloadOptions[0].onClick}
        />
      );
    } else {
      return (
        <AgxMultiOptionButton
          id="viewContractPdfs"
          text="Download"
          hollow
          medium
          options={fileDownloadOptions}
        />
      );
    }
  };

  const errorInfo = (
    <AgxRow centered veryLargeGap extraClasses={'toastStyle'} fill={isMobile}>
      <span>
        <Images.AlertCircle />
      </span>
      <AgxBodyText>
        {`Please complete required fields before ${sendViaDocuSign ? 'Docusigning' : 'sending'}`}
        <br />
        <AgxButton
          id="btn-view-details"
          text="View Details"
          variant="link"
          onClick={() => setOpenErrorModal(true)}
        ></AgxButton>
      </AgxBodyText>
    </AgxRow>
  );

  const onPreviousClicked = () => {
    goToPreviousPage();
  };

  const isPdfEditorSaving = pdfEditorSaveStatus === 'saving';
  const closePdfEditor = () => setShowPdfEditor(false);
  const cancelPdfEditor = () => {
    if (isPdfEditorSaving) return;
    setShowPdfEditor(false);
  };
  const setPdfEditorOpen = (open: boolean) => {
    if (!open && isPdfEditorSaving) return;
    setShowPdfEditor(open);
  };

  const getGeneratedContractFileLink = async (filePath: string) =>
    (await getFileLink(filePath)) ?? undefined;

  const uploadEditedContractPdf = async ({ file }: { file: File }) => {
    if (!campaignId || !formId || !generatedContract?.containerFilePath) {
      throw new Error('Unable to save edited contract.');
    }

    const authToken = await getAuthToken();

    if (!authToken) {
      throw new Error('Failed to login');
    }

    const formData = new FormData();
    formData.append('File', file);

    const uploadedDocument = (await uploadFile(
      authToken,
      formData,
      campaignId,
      DocumentTypes.ContractExchange
    )) as UploadedDocument | undefined;

    if (!uploadedDocument) {
      throw new Error('Unable to save edited contract.');
    }

    dispatch(
      updateGeneratedFormDocument({
        campaignId,
        formId,
        uploadedDocument,
      })
    );

    return uploadedDocument;
  };

  const handlePdfEditorError = (error: unknown) => {
    const message =
      error instanceof Error
        ? error.message
        : 'Unable to save edited contract.';
    dispatch(setAndShowErrorToast(message));
  };

  if (showDocuSignLoader)
    return (
      <AgxColumn
        fill
        centerJustified
        centered
        extraClasses="sendForSigningStatusContent"
      >
        <RenderAnimation icon={RenderAnimationType.Paper} />
        <AgxColumn fill centered largeGap>
          <AgxHeader size={1} inverse>
            Just a sec
          </AgxHeader>
          <AgxHeader size={3} secondary>
            {`Sending ${formName}...`}
          </AgxHeader>
        </AgxColumn>
      </AgxColumn>
    );

  if (showSuccessLoader)
    return (
      <AgxColumn
        fill
        centerJustified
        centered
        extraClasses="sendForSigningStatusContent"
      >
        <RenderAnimation icon={RenderAnimationType.Champagne} />
        <AgxHeader size={1} inverse>
          Success!
        </AgxHeader>
      </AgxColumn>
    );

  if (showRecipients)
    return (
      <SendConfirmation
        buyers={buyerSignatories}
        buyerSolicitor={buyerSolicitorRecipient}
        vendors={vendorSignatories}
        vendorSolicitor={vendorSolicitorRecipient}
        onBack={() => setShowRecipients(false)}
        onSend={(
          sendVendorsNow,
          signingOrderGroups,
          includeSolicitorsInSalesAdvice
        ) =>
          sendEnvelope(
            sendVendorsNow,
            signingOrderGroups,
            includeSolicitorsInSalesAdvice
          )
        }
        sendViaDocuSign={sendViaDocuSign}
        sendConfiguration={configuration}
        isMobile={isMobile}
        enableSigningOrderSelection={isContractExchange}
        enableSalesAdviceSolicitorSelection={isContractExchange}
      />
    );

  return (
    <AgxColumn extraClasses="reviewAndSignImageCard">
      <AgxColumn
        veryLargeGap
        extraLargePadding
        centered
        extraClasses="agreementDetails"
      >
        {isNswContractExchange && generatedContract ? (
          <div className="contractExchangePreview">
            <img
              className="contractExchangePreviewImage"
              alt="Contract preview"
              src={contractPreview}
            />
            <div className="contractExchangePreviewOverlay">
              {viewEditContractButton('primary')}
            </div>
          </div>
        ) : sendViaDocuSign ? (
          <img
            className="propertyImage"
            alt="Avatar"
            src={medium || placeHolderImage}
          />
        ) : (
          <Images.SparkleForm />
        )}
        <AgxRow largeGap centered justifyCenter={isMobile} fill={isMobile}>
          {viewPdfButton()}
          {!isContractExchange && (
            <AgxButton
              id="btn-edit"
              text="Edit"
              onClick={goToFirstPage}
              variant="hollow"
              size="medium"
              disabled={disableButtons}
            />
          )}
        </AgxRow>
      </AgxColumn>
      {sendViaDocuSign || sendConfigurations?.length > 1 ? (
        <div className="signingGrid">
          <AgxColumn largeGap centered extraClasses="signingGridCell">
            <AgxLabel large>Docusign</AgxLabel>
            {sendConfigurations.map((config, index) => (
              <AgxButton
                id={`btn-${config.title}-${index}`}
                key={`btn-${config.title}-${index}`}
                variant="primary"
                size="large"
                text={config.title}
                onClick={() => {
                  if (errors.length > 0) {
                    setOpenErrorModal(true);
                  } else {
                    setShowRecipients(true);
                    setConfiguration(config);
                  }
                }}
                disabled={disableButtons}
                stretch
              />
            ))}
            {errors.length > 0 && errorInfo}
          </AgxColumn>
          {!disableFormSubmission && (
            <AgxColumn largeGap centered extraClasses="signingGridCell">
              <AgxLabel large>Sign in person</AgxLabel>
              <AgxButton
                id="btn-download-to-sign"
                variant="primary"
                size="large"
                text="Download to Sign"
                onClick={
                  fileDownloadOptions.length > 0
                    ? fileDownloadOptions[0].onClick
                    : undefined
                }
                stretch
                disabled={!fileDownloadOptions.length}
              />
              {fileDownloadOptions.length > 0 && (
                <AgxButton
                  id="btn-submit-signed-pdf"
                  variant="hollow"
                  size="large"
                  text="Submit Signed PDF"
                  onClick={() => setShowConfirmationModel(true)}
                  disabled={disableButtons}
                  stretch
                />
              )}
            </AgxColumn>
          )}
        </div>
      ) : (
        <>
          {errors.length > 0 && (
            <AgxColumn extraClasses={'alignColumn'}>{errorInfo}</AgxColumn>
          )}
          <div className="emailGrid borderTop">
            <AgxColumn centered>
              <AgxRow largeGap fill justifyCenter>
                <AgxButton
                  id="btn-review-back"
                  variant="hollow"
                  size="large"
                  text="Back"
                  onClick={onPreviousClicked}
                />
                <AgxButton
                  id="btn-review-next"
                  variant="primary"
                  size="large"
                  text="Next"
                  onClick={() => {
                    setShowRecipients(true);
                    setConfiguration(sendConfigurations[0]);
                  }}
                  disabled={errors.length > 0}
                />
              </AgxRow>
            </AgxColumn>
          </div>
        </>
      )}

      {openErrorModal && (
        <ErrorModal errorList={errors} setShowModal={setOpenErrorModal} />
      )}

      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}

      {generatedContract && (
        <AgxModal
          id="contract-pdf-editor-modal"
          title={`Editing Contract for ${listingAddress}`}
          type="fullHeight"
          variant="withDivider"
          actionButtonContainerStyle="withoutDivider"
          minHeight="min(720px, calc(100vh - 160px))"
          minWidth="min(1180px, calc(100vw - 96px))"
          maxWidth="calc(100vw - (2 * var(--spacing-10xl)))"
          isOpen={showPdfEditor}
          setIsDialogOpen={setPdfEditorOpen}
          content={
            <AgxUploadedDocumentPdfEditor
              id="contract-exchange-pdf-editor"
              uploadedDocument={generatedContract}
              pdfFileName={generatedContract.fileName ?? 'Contract.pdf'}
              getFileLink={getGeneratedContractFileLink}
              uploadEditedPdf={uploadEditedContractPdf}
              onCancel={cancelPdfEditor}
              onSaveStatusChange={setPdfEditorSaveStatus}
              onSaved={closePdfEditor}
              onError={handlePdfEditorError}
              showAddText
              showStrikeOut
              fieldBlockTemplates={contractExchangeFieldBlockTemplates}
              showActionButtons
              cancelButtonText="Cancel"
              saveButtonText="Save"
            />
          }
        />
      )}
    </AgxColumn>
  );
};

export default ContractReviewAndSign;
